import { Base } from '@studiometa/js-toolkit';
import { gsap } from 'gsap';
import { push } from '../utils/history.js';
import Cache from '../utils/Cache.js';

const HTML = document.querySelector('html');

/**
 * @typedef {object} FiltersRefs
 * @property {HTMLFormElement} form
 * @property {HTMLElement} loader
 * @property {HTMLElement} pagination
 * @property {HTMLElement} subcategory
 * @property {HTMLElement[]} trigger
 * @property {HTMLElement} results
 * @property {HTMLElement} filter
 * @property {HTMLElement} filterOpen
 * @property {HTMLElement} filterClose
 * @typedef {object} FiltersInterface
 * @property {FiltersRefs} $refs
 */

/**
 * Filters class.
 */
export default class Filters extends Base {
  static config = {
    name: 'Filters',
    log: true,
    refs: [
      'form',
      'loader',
      'results',
      'pagination',
      'subcategory',
      'trigger[]',
      'filter',
      'filterOpen',
      'filterClose',
    ],
  };

  /**
   * A helper to parse the requests' response.
   * @type {DOMParser}
   */
  parser = new DOMParser();

  cache = new Cache({ name: 'Filters' });

  onloading = false;

  /**
   * Listen to popstate.
   */
  mounted() {
    this.boundPopstateHandler = this.onWindowPopstate.bind(this);
    window.addEventListener('popstate', this.boundPopstateHandler);
  }

  /**
   * Listen to scroll.
   */
  scrolled() {
    // Infinite loading
    if (this.$refs.pagination) {
      const pagination =
        this.$refs.pagination.length === undefined
          ? this.$refs.pagination
          : this.$refs.pagination.at(-1);
      const pos = pagination.getBoundingClientRect();
      if (pos.y - window.innerHeight <= 600 && !this.onloading) {
        this.onloading = true;
        const link = pagination.querySelector('a');

        if (link) {
          const e = new Event('click');
          Object.defineProperty(e, 'target', { writable: false, value: link });
          this.onPaginationClick(e);
        }
      }
    }
  }

  /**
   * Stop to listen to popstate.
   */
  destroyed() {
    window.removeEventListener('popstate', this.boundPopstateHandler);
  }

  /**
   * Update the content when an history state is changed.
   */
  onWindowPopstate() {
    this.getAndUpdateContent();
  }

  /**
   * Handle filter label clicks
   * For array refs, the toolkit automatically passes the index as the second parameter
   * @param {MouseEvent} event
   * @this {Filters & FiltersInterface}
   */
  onTriggerClick(event) {
    if (event && typeof event.preventDefault === 'function') {
      event.preventDefault();
    }

    setTimeout(() => {
      this.handleFilterChange();
    }, 100);
  }

  /**
   * Handle form changes (when inputs are toggled)
   * @this {Filters & FiltersInterface}
   */
  handleFilterChange() {
    const { pathname } = new URL(this.$refs.form.action);
    const formData = new FormData(this.$refs.form);
    const search = Array.from(formData.entries()).reduce((acc, [key, value]) => {
      if (value !== '' && typeof value === 'string') {
        acc.append(key, value);
      }
      return acc;
    }, new URLSearchParams());

    push({ path: pathname, search });
    this.getAndUpdateContent();
  }

  /**
   * Prevent form submission and handle via AJAX instead
   * @param {SubmitEvent} event
   * @this {Filters & FiltersInterface}
   */
  onFormSubmit(event) {
    event.preventDefault();
    event.stopPropagation();

    this.handleFilterChange();
    return false;
  }

  /**
   * Capture pagination clicks to navigate via ajax.
   * @param {MouseEvent} event The event object.
   */
  async onPaginationClick(event) {
    const target = event.target.nodeName === 'A' ? event.target : event.target.closest('a');

    if (!target) {
      return;
    }

    if (event && typeof event.preventDefault === 'function') {
      event.preventDefault();
    }
    // HTML.classList.add('show-overlay');
    this.$refs.loader.classList.remove('opacity-0');
    const oldPathname = window.location.pathname;
    push({ path: target.pathname, search: new URLSearchParams(target.search) });
    gsap.to(this.$refs.pagination, {
      height: 0,
      duration: 0,
      onComplete: () => this.$refs.pagination.remove(),
    });
    await this.getAndUpdateContent(false);
    push({ path: oldPathname });
    this.$refs.loader.classList.add('opacity-0');

    // Scroll to start new result
    const childs = this.$refs.results.children;
    const bounds = childs.at(-2).getBoundingClientRect();
    if (bounds.bottom <= window.innerHeight) {
      gsap.to(document.querySelectorAll('html, body'), {
        duration: 0.4,
        scrollTop: window.scrollY + bounds.bottom - window.innerHeight * 0.7,
        onComplete: () => {
          setTimeout(() => {
            this.onloading = false;
          }, 400);
        },
      });
    } else {
      this.onloading = false;
    }
  }

  /**
   * Get the new results.
   * @param {boolean} clear Override the content or not.
   * @this {Filters & FiltersInterface}
   */
  async getAndUpdateContent(clear = true) {
    const text = await fetch(window.location.href).then((response) => response.text());
    const doc = this.parser.parseFromString(text, 'text/html');
    const newForm = doc.querySelector('[data-component="Filters"] [data-ref="form"]');
    const newResults = doc.querySelector('[data-component="Filters"] [data-ref="results"]');

    if (newResults) {
      if (clear) {
        this.$refs.results.innerHTML = newResults.innerHTML;
      } else {
        const result = document.createElement('div');
        result.innerHTML = newResults.innerHTML;
        this.$refs.results.append(result);
      }
    }

    if (newForm) {
      this.$refs.form.innerHTML = newForm.innerHTML;
    }

    const urlParams = new URLSearchParams(window.location.search);

    this.$refs.form
      .querySelectorAll('input[type="radio"], input[type="checkbox"]')
      .forEach((input) => {
        const urlValue = urlParams.get(input.name);

        if (input.value === '') {
          input.checked = !urlValue || urlValue === '';
        } else {
          input.checked = urlValue === input.value;
        }
      });

    HTML.classList.remove('show-overlay');
    this.$update();
  }

  /**
   * Open/close the sub tax
   * @param {Event} e
   */
  onSubcategoryClick(e) {
    if (e && typeof e.preventDefault === 'function') {
      e.preventDefault();
    }

    const next = this.$refs.subcategory.nextElementSibling;
    if (next) {
      next.classList.toggle('opacity-0');
      next.classList.toggle('invisible');
      next.classList.toggle('-translate-y-2');
    }
  }

  /**
   * Open filter
   * @param {Event} e
   */
  onFilterOpenClick(e) {
    if (e && typeof e.preventDefault === 'function') {
      e.preventDefault();
    }
    this.$refs.filter.classList.remove('translate-x-full');
  }

  /**
   * Close filter
   * @param {Event} e
   */
  onFilterCloseClick(e) {
    if (e && typeof e.preventDefault === 'function') {
      e.preventDefault();
    }
    this.$refs.filter.classList.add('translate-x-full');
  }
}
